import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { AddressAutocompleteInput } from "../lib/addressAutocomplete.js";
import type { ResolvedAddress } from "../lib/addressAutocomplete.js";
import { DatePicker } from "../components/DatePicker.js";
import { ADMIN_ROLES, HR_ROLES, SALARY_ROLES } from "@hrms/types";
import { MapPin, Lock } from "lucide-react";
import { AsyncButton } from "../components/ui/async-button.js";
import { SelectField } from "../components/ui/select-field.js";
import { Pagination } from "../components/ui/pagination.js";
import { usePaged } from "../lib/usePaged.js";
import { TimeInput } from "../components/ui/time-input.js";
import { usePageBack } from "../context/PageBackContext.js";
import SkillsCatalogPanel from "./performance/SkillsCatalogPanel.js";
import CompRulesPanel from "./admin/CompRulesPanel.js";
import { AttendanceAdminPanel } from "../components/AttendanceAdminPanel.js";
import PolicyTextsPanel from "./admin/PolicyTextsPanel.js";
import FestiveEventsPanel from "./admin/FestiveEventsPanel.js";
import EmployeeBulkUploadPanel from "./admin/EmployeeBulkUploadPanel.js";
import DesignationsPanel from "./admin/DesignationsPanel.js";
import PolicyDocumentsPanel from "./admin/PolicyDocumentsPanel.js";

function decodeToken(token: string): { role: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? "") };
  } catch { return { role: "" }; }
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};
const btnStyle: React.CSSProperties = {
  padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
  fontWeight: 600, cursor: "pointer", border: "none",
  background: "var(--color-brand-600)", color: "#fff",
};

interface Department { id: string; name: string; code: string; office: { name: string }; head: { firstName: string; lastName: string } | null; _count: { employees: number; children: number; workforcePlanEntries: number } }
interface Office {
  id: string; name: string; city: string; country: string; timezone: string; currency: string;
  addressLine1: string | null; addressLine2: string | null; pincode: string | null;
  geoLat: number | null; geoLng: number | null; geofenceRadiusM: number; lateThresholdHHMM: string;
  _count: { employees: number; departments: number }
}
interface OverheadConfig { id: string; category: string; ratePercent: string; effectiveFrom: string }
interface EmpRow { id: string; employeeCode: string; firstName: string; lastName: string; email: string; role: string; status: string; department: { name: string } | null }
interface LookupRow { id: string; category: string; value: string; displayName: string; rank: number; isActive: boolean; isSystem: boolean }
interface PublicHoliday { id: string; date: string; name: string; country: string; year: number; isOptional: boolean }


const LOOKUP_CATEGORY_LABELS: Record<string, string> = {
  WORK_LOCATION: "Work Locations", LEAVE_TYPE: "Leave Types",
  EXPENSE_CATEGORY: "Expense Types", ASSET_CATEGORY: "Asset Categories",
  PETTY_CASH_TYPE: "Petty Cash Types (merged into Expense Types)", PETTY_CASH_MODE: "Payment Modes",
  TICKET_CATEGORY: "IT Ticket Categories", GRIEVANCE_CATEGORY: "Grievance Categories",
  COURSE_CATEGORY: "Training Categories", DOCUMENT_TYPE: "Document Types",
  OFFBOARDING_REASON: "Offboarding Reasons", CONTRACTOR_TAX_ID_TYPE: "Tax ID Types",
  CONTRACTOR_RATE_TYPE: "Rate Types", CURRENCY: "Currencies",
  EMPLOYEE_TITLE: "Name Titles", GENDER: "Gender Options",
  MARITAL_STATUS: "Marital Status", TSHIRT_SIZE: "T-Shirt Sizes",
  EMERGENCY_RELATIONSHIP: "Emergency Relationships", ROLE_LABEL: "Role Display Labels",
  DESIGNATION: "Designations", POLICY_DOCUMENT: "Policy Documents",
};

const TABS = ["Role Assignment", "Departments", "Offices", "Designations", "Overhead Config", "Dropdown Config", "Leave Policies", "Holidays", "Festive Events", "Attendance", "Compensation Rules", "Policy Documents", "Policy Texts", "Skills", "Bulk Upload"] as const;
// Old ?tab= names (links in notifications/bookmarks) open the merged tab.
const TAB_ALIASES: Record<string, string> = { "Comp Rules": "Compensation Rules", "Compensation": "Compensation Rules", "Handbook": "Policy Documents" };
type Tab = typeof TABS[number];

export default function AdminPage() {
  usePageBack("/dashboard");
  const navigate = useNavigate();
  const accessToken = getAccessToken() ?? "";
  const { role } = decodeToken(accessToken);
  const isAdmin = ADMIN_ROLES.has(role);
  const isHRViewer = HR_ROLES.has(role);
  // Compensation Rules follows the compensation API's SALARY_ROLES gate: HR roles outside
  // it (Regional HR) don't get the tab, and Payroll/Finance get only that tab (Finance
  // verifies salary-structure rule changes there).
  const canView = isHRViewer || SALARY_ROLES.has(role);
  // Attendance holds the org-wide approval switch, which is super HR admin only.
  const visibleTabs: readonly Tab[] = isHRViewer
    ? TABS.filter(t =>
        (t !== "Compensation Rules" || SALARY_ROLES.has(role)) &&
        (t !== "Attendance" || role === "SUPER_HR_ADMIN") &&
        // Opens Leave Admin, which is admin-only -- other HR roles would just bounce back.
        (t !== "Leave Policies" || isAdmin) &&
        // Festive events are written only by ADMIN_ROLES, so the tab is admin-only too.
        (t !== "Festive Events" || isAdmin) &&
        // Bulk upload can change roles, so it is admin-only like the API behind it.
        (t !== "Bulk Upload" || isAdmin))
    : ["Compensation Rules"];

  // ?tab=Skills opens a tab directly (the old /performance/skills link redirects here).
  const [tab, setTab] = useState<Tab>(() => {
    const raw = new URLSearchParams(window.location.search).get("tab") ?? "";
    const q = TAB_ALIASES[raw] ?? raw;
    return (visibleTabs as readonly string[]).includes(q) ? (q as Tab) : visibleTabs[0]!;
  });
  // Leave policies live in Leave Admin -- an old ?tab=Leave Policies link goes straight there.
  useEffect(() => {
    if (tab === "Leave Policies") navigate("/admin/leave", { replace: true });
  }, [tab, navigate]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [employees, setEmployees] = useState<EmpRow[]>([]);
  const [empSearch, setEmpSearch] = useState("");
  const [empTotal, setEmpTotal] = useState(0);
  const [empPage, setEmpPage] = useState(1);
  const [empLimit, setEmpLimit] = useState(25);

  const [departments, setDepartments] = useState<Department[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [overhead, setOverhead] = useState<OverheadConfig[]>([]);

  const [deptForm, setDeptForm] = useState({ name: "", code: "", officeId: "" });
  const blankOfficeForm = { name: "", city: "", country: "India", timezone: "Asia/Kolkata", currency: "INR", addressLine1: "", addressLine2: "", pincode: "", geoLat: null as number | null, geoLng: null as number | null, geofenceRadiusM: 500, lateThresholdHHMM: "09:30" };
  const [officeForm, setOfficeForm] = useState(blankOfficeForm);
  const [editingOfficeId, setEditingOfficeId] = useState<string | null>(null);
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [showOfficeForm, setShowOfficeForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [overheadEdits, setOverheadEdits] = useState<Record<string, string>>({});

  const [lookupRows, setLookupRows] = useState<LookupRow[]>([]);
  const [lookupFilter, setLookupFilter] = useState("ALL");
  const [showAddLookup, setShowAddLookup] = useState(false);
  const [addLookupForm, setAddLookupForm] = useState({ category: "", value: "", displayName: "", rank: "0" });
  const [editingLookupId, setEditingLookupId] = useState<string | null>(null);
  const [editLookupForm, setEditLookupForm] = useState({ displayName: "", rank: "0" });


  const blankHolidayForm = { date: "", name: "", isOptional: false };
  const [holidays,         setHolidays]         = useState<PublicHoliday[]>([]);
  const [holidayYear,      setHolidayYear]      = useState(2026);
  const [holidayForm,      setHolidayForm]      = useState(blankHolidayForm);
  const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);
  const [showHolidayForm,  setShowHolidayForm]  = useState(false);


  // `silent` skips the loading flag so the search input stays mounted (and focused) while typing.
  // Client-side pages over the fully loaded config lists; shared rows-per-page.
  const [adminPageSize, setAdminPageSize] = useState(25);
  const deptPaged     = usePaged(departments, `dept|${adminPageSize}`, adminPageSize);
  const officePaged   = usePaged(offices, `office|${adminPageSize}`, adminPageSize);
  const overheadPaged = usePaged(overhead, `overhead|${adminPageSize}`, adminPageSize);
  const holidayPaged  = usePaged(holidays, `holiday|${holidayYear}|${adminPageSize}`, adminPageSize);
  const lookupShown   = lookupRows.filter(r => lookupFilter === "ALL" || r.category === lookupFilter);
  const lookupPaged   = usePaged(lookupShown, `lookup|${lookupFilter}|${adminPageSize}`, adminPageSize);

  async function loadTab(t: Tab, silent = false, pageOverride?: number) {
    if (!silent) setLoading(true);
    setError("");
    try {
      if (t === "Role Assignment") {
        const r = await api.get<{ data: EmpRow[]; meta: { total: number } }>(`/admin/employees?limit=${empLimit}&page=${pageOverride ?? empPage}&search=${encodeURIComponent(empSearch)}`);
        setEmployees(r.data.data); setEmpTotal(r.data.meta.total);
      } else if (t === "Departments") {
        const [deptRes, offRes] = await Promise.all([
          api.get<Department[]>("/admin/departments"),
          api.get<Office[]>("/admin/offices"),
        ]);
        setDepartments(deptRes.data);
        setOffices(offRes.data);
      } else if (t === "Offices") {
        const r = await api.get<Office[]>("/admin/offices");
        setOffices(r.data);
      } else if (t === "Overhead Config") {
        const r = await api.get<OverheadConfig[]>("/admin/overhead");
        setOverhead(r.data);
        const edits: Record<string, string> = {};
        r.data.forEach(o => { edits[o.id] = o.ratePercent; });
        setOverheadEdits(edits);
      } else if (t === "Dropdown Config") {
        const r = await api.get<LookupRow[]>("/admin/lookup");
        setLookupRows(r.data);
      } else if (t === "Holidays") {
        const r = await api.get<PublicHoliday[]>(`/admin/holidays?year=${holidayYear}`);
        setHolidays(r.data);
      }
    } catch (err) { logError("AdminPage.loadTab", err); setError("Failed to load data"); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (canView) loadTab(tab); }, [tab]);

  // Button / Enter search: runs immediately at any length.
  async function searchEmployees() { setEmpPage(1); await loadTab("Role Assignment", true, 1); }

  // Live search: starts at 4 characters, re-runs on each further keystroke; empty box lists everyone.
  const EMP_SEARCH_MIN = 4;
  useEffect(() => {
    if (!canView || tab !== "Role Assignment") return;
    const q = empSearch.trim();
    if (q.length > 0 && q.length < EMP_SEARCH_MIN) return;
    const id = setTimeout(() => { void loadTab("Role Assignment", true); }, 300);
    return () => clearTimeout(id);
  }, [empSearch, empPage, empLimit]);

  async function updateRole(empId: string, newRole: string) {
    setSaving(true); setError(""); setSuccess("");
    try {
      await api.patch(`/admin/employees/${empId}/role`, { role: newRole });
      setSuccess("Role updated");
      loadTab("Role Assignment");
    } catch (err) { logError("AdminPage.updateRole", err); setError("Failed to update role"); }
    finally { setSaving(false); }
  }

  async function createDept() {
    setSaving(true); setError("");
    try {
      await api.post("/admin/departments", deptForm);
      setShowDeptForm(false); setDeptForm({ name: "", code: "", officeId: "" });
      setSuccess("Department created"); loadTab("Departments");
    } catch (err) { logError("AdminPage.createDept", err); setError("Failed to create department"); throw err; }
    finally { setSaving(false); }
  }

  async function deleteDept(id: string, name: string) {
    if (!confirm(`Delete department "${name}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api.delete(`/admin/departments/${id}`);
      setSuccess("Department deleted"); loadTab("Departments");
    } catch (err) {
      logError("AdminPage.deleteDept", err);
      setError(errMsg(err) ?? "Failed to delete department");
    }
  }

  async function createOffice() {
    setSaving(true); setError("");
    try {
      await api.post("/admin/offices", { ...officeForm, geofenceRadiusM: Number(officeForm.geofenceRadiusM) });
      setShowOfficeForm(false); setOfficeForm(blankOfficeForm);
      setSuccess("Office created"); loadTab("Offices");
    } catch (err) { logError("AdminPage.createOffice", err); setError("Failed to create office"); throw err; }
    finally { setSaving(false); }
  }

  async function saveOfficeEdit() {
    if (!editingOfficeId) throw new Error("validation");
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/offices/${editingOfficeId}`, { ...officeForm, geofenceRadiusM: Number(officeForm.geofenceRadiusM) });
      setEditingOfficeId(null); setShowOfficeForm(false); setOfficeForm(blankOfficeForm);
      setSuccess("Office updated"); loadTab("Offices");
    } catch (err) { logError("AdminPage.saveOfficeEdit", err); setError("Failed to update office"); throw err; }
    finally { setSaving(false); }
  }

  async function deleteOffice(id: string, name: string) {
    if (!confirm(`Delete office "${name}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api.delete(`/admin/offices/${id}`);
      setSuccess("Office deleted"); loadTab("Offices");
    } catch (err) {
      logError("AdminPage.deleteOffice", err);
      setError(errMsg(err) ?? "Failed to delete office");
    }
  }

  async function createHoliday() {
    setSaving(true); setError("");
    try {
      await api.post("/admin/holidays", holidayForm);
      setShowHolidayForm(false); setHolidayForm(blankHolidayForm);
      setSuccess("Holiday created"); loadTab("Holidays");
    } catch (err) { logError("AdminPage.createHoliday", err); setError("Failed to create holiday"); throw err; }
    finally { setSaving(false); }
  }

  async function saveHolidayEdit() {
    if (!editingHolidayId) throw new Error("validation");
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/holidays/${editingHolidayId}`, holidayForm);
      setEditingHolidayId(null); setShowHolidayForm(false); setHolidayForm(blankHolidayForm);
      setSuccess("Holiday updated"); loadTab("Holidays");
    } catch (err) { logError("AdminPage.saveHolidayEdit", err); setError("Failed to update holiday"); throw err; }
    finally { setSaving(false); }
  }

  async function deleteHoliday(id: string) {
    if (!confirm("Delete this holiday?")) return;
    setSaving(true); setError("");
    try {
      await api.delete(`/admin/holidays/${id}`);
      setSuccess("Holiday deleted"); loadTab("Holidays");
    } catch (err) { logError("AdminPage.deleteHoliday", err); setError("Failed to delete holiday"); }
    finally { setSaving(false); }
  }

  async function saveOverhead(id: string) {
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/overhead/${id}`, { ratePercent: parseFloat(overheadEdits[id] ?? "0") });
      setSuccess("Overhead rate updated"); loadTab("Overhead Config");
    } catch (err) { logError("AdminPage.saveOverhead", err); setError("Failed to update rate"); }
    finally { setSaving(false); }
  }

  async function addLookupValue() {
    setSaving(true); setError("");
    try {
      await api.post("/admin/lookup", {
        category: addLookupForm.category,
        value: addLookupForm.value,
        displayName: addLookupForm.displayName,
        rank: parseInt(addLookupForm.rank, 10) || 0,
      });
      setSuccess("Value added"); setShowAddLookup(false);
      setAddLookupForm({ category: "", value: "", displayName: "", rank: "0" });
      loadTab("Dropdown Config");
    } catch (e: unknown) {
      logError("AdminPage.addLookupValue", e);
      const msg = errMsg(e);
      setError(msg ?? "Failed to add value");
      throw e;
    }
    finally { setSaving(false); }
  }

  async function saveLookupEdit(id: string) {
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/lookup/${id}`, {
        displayName: editLookupForm.displayName,
        rank: parseInt(editLookupForm.rank, 10) || 0,
      });
      setSuccess("Updated"); setEditingLookupId(null);
      loadTab("Dropdown Config");
    } catch (err) { logError("AdminPage.saveLookupEdit", err); setError("Failed to update"); }
    finally { setSaving(false); }
  }

  async function toggleLookupActive(row: LookupRow) {
    setSaving(true); setError("");
    try {
      await api.patch(`/admin/lookup/${row.id}`, { isActive: !row.isActive });
      setSuccess(row.isActive ? "Deactivated" : "Activated");
      loadTab("Dropdown Config");
    } catch (err) { logError("AdminPage.toggleLookupActive", err); setError("Failed to update"); }
    finally { setSaving(false); }
  }

  async function deleteLookupValue(id: string) {
    if (!confirm("Delete this value?")) return;
    setSaving(true); setError("");
    try {
      await api.delete(`/admin/lookup/${id}`);
      setSuccess("Deleted"); loadTab("Dropdown Config");
    } catch (e: unknown) {
      logError("AdminPage.deleteLookupValue", e);
      const msg = errMsg(e);
      setError(msg ?? "Failed to delete");
    }
    finally { setSaving(false); }
  }

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
    fontWeight: active ? 700 : 500, cursor: "pointer",
    border: "1px solid " + (active ? "var(--color-brand-600)" : "var(--color-surface-200)"),
    background: active ? "var(--color-brand-600)" : "var(--color-surface-0)",
    color: active ? "#fff" : "var(--color-surface-700)",
  });

  if (!canView) return <div style={{ padding: "32px" }}>Access denied.</div>;

  return (
    <div className="mx-auto w-full max-w-[1100px] p-4 min-[1536px]:max-w-[1600px] min-[1920px]:max-w-[2000px] min-[1920px]:p-5">
      <h1 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "24px" }}>Administration</h1>

      {error && (
        <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: "12px", fontSize: "13px" }}>
          {error} <button style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "var(--color-error-fg)" }} onClick={() => setError("")}>×</button>
        </div>
      )}
      {success && (
        <div style={{ background: "var(--color-success-bg)", color: "var(--color-success-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: "12px", fontSize: "13px" }}>
          {success} <button style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "var(--color-success-fg)" }} onClick={() => setSuccess("")}>×</button>
        </div>
      )}

      {/* ─── Quick Links ─── */}
      {isHRViewer && <div className="mb-5 grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-2">
        {[
          { label: "Pending Change Requests", sub: "Approve / reject field changes", path: "/admin/pending-changes", color: "var(--color-warning-fg)", bg: "var(--color-warning-bg)", border: "var(--color-warning-border)" },
          { label: "Field Permissions", sub: "Permission matrix, temp grants, field registry", path: "/admin/field-permissions", color: "var(--color-info-fg)", bg: "var(--color-info-bg)", border: "var(--color-info-border)" },
          { label: "Audit Log", sub: "Full system-wide change history", path: "/admin/audit", color: "var(--color-success-fg)", bg: "var(--color-success-bg)", border: "var(--color-success-border)" },
          { label: "Attendance Sources", sub: "Biometric feeds — import, explore, ingest", path: "/admin/attendance-sources", color: "#0f766e", bg: "#ccfbf1", border: "#5eead4" },
          { label: "Issues", sub: "All issues, my issues, impact, leaderboard", path: "/issues", color: "var(--color-error-fg)", bg: "var(--color-error-bg)", border: "var(--color-error-border)" },
          // Admin roles only, matching the participants API; other HR viewers would get a 403.
          ...(isAdmin ? [{ label: "Issue Tracker Settings", sub: "Engineer team, assignees, issue emails", path: "/admin/issue-tracker", color: "var(--color-error-fg)", bg: "var(--color-error-bg)", border: "var(--color-error-border)" }] : []),
          // Admin roles only, like the page itself; a browser-only simulation of tools/migration-v2.
          ...(isAdmin ? [{ label: "Migration v2 Simulator", sub: "Try batch legacy migrations on synthetic data", path: "/admin/migration-v2", color: "var(--color-info-fg)", bg: "var(--color-info-bg)", border: "var(--color-info-border)" }] : []),
          // Super HR admin only; the page enforces it too, this just hides the card.
          ...(role === "SUPER_HR_ADMIN" ? [{ label: "Virtual HR", sub: "Sirra: document prompts, name, on / off", path: "/admin/virtual-hr", color: "var(--color-accent-fg)", bg: "var(--color-accent-bg)", border: "var(--color-accent-border)" }] : []),
          ...(role === "SUPER_HR_ADMIN" ? [{ label: "Shift Change History", sub: "Who changed whose shift, org-wide", path: "/admin/shift-history", color: "var(--color-accent-fg)", bg: "var(--color-accent-bg)", border: "var(--color-accent-border)" }] : []),
        ].map((card) => (
          <button
            key={card.path}
            onClick={() => navigate(card.path)}
            style={{
              background: card.bg, border: `1px solid ${card.border}`,
              borderRadius: "var(--radius-md)", padding: "8px 12px", cursor: "pointer",
              textAlign: "left", display: "flex", flexDirection: "column", gap: "1px", minWidth: 0,
            }}
            title={card.sub}
          >
            <span style={{ fontSize: "13px", fontWeight: 700, color: card.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{card.label}</span>
            <span style={{ fontSize: "11px", color: card.color, opacity: 0.75, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{card.sub}</span>
          </button>
        ))}
      </div>}

      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
        {visibleTabs.map(t => <button key={t} style={tabBtnStyle(tab === t)} onClick={() => (t === "Leave Policies" ? navigate("/admin/leave") : setTab(t))}>{t}</button>)}
      </div>

      {loading && <p>Loading…</p>}

      {/* ─── Skills (catalog used by every performance review) ─── */}
      {!loading && tab === "Skills" && <SkillsCatalogPanel canEdit={isHRViewer} />}

      {/* ─── Bulk Upload (role, designation, reporting manager from an Excel sheet) ─── */}
      {!loading && tab === "Bulk Upload" && isAdmin && <EmployeeBulkUploadPanel />}

      {/* ─── Policy Texts (data-protection notice and retention wording) ─── */}
      {!loading && tab === "Policy Texts" && <PolicyTextsPanel role={role} />}

      {/* ─── Festive Events (festivals and company events, shown per branch) ─── */}
      {!loading && tab === "Festive Events" && <FestiveEventsPanel />}

      {/* ─── Designations (the employee form's designation dropdown) ─── */}
      {!loading && tab === "Designations" && <DesignationsPanel canEdit={isAdmin} />}

      {/* ─── Role Assignment ─── */}
      {!loading && tab === "Role Assignment" && (
        <div>
          <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
            <input style={{ ...inputStyle, maxWidth: "300px" }} placeholder="Search name, email, code…" value={empSearch} onChange={e => { setEmpSearch(e.target.value); setEmpPage(1); }}
              onKeyDown={e => e.key === "Enter" && searchEmployees()} />
            <button style={btnStyle} onClick={searchEmployees}>Search</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Code", "Name", "Email", "Department", "Current Role", "Assign Role"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map(e => (
                <RoleRow key={e.id} emp={e} isAdmin={isAdmin} onSave={updateRole} saving={saving} />
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={6} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No employees found.</td></tr>
              )}
            </tbody>
          </table>
          <Pagination
            meta={{ total: empTotal, page: empPage, limit: empLimit, totalPages: Math.max(1, Math.ceil(empTotal / empLimit)) }}
            onPageChange={setEmpPage}
            onPageSizeChange={(n) => { setEmpLimit(n); setEmpPage(1); }}
          />
        </div>
      )}

      {/* ─── Departments ─── */}
      {!loading && tab === "Departments" && (
        <div>
          {isAdmin && !showDeptForm && (
            <button style={{ ...btnStyle, marginBottom: "16px" }} onClick={() => setShowDeptForm(true)}>+ New Department</button>
          )}
          {showDeptForm && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "14px" }}>New Department</h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                <div><label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Name *</label>
                  <input style={inputStyle} value={deptForm.name} onChange={e => setDeptForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div><label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Code *</label>
                  <input style={inputStyle} value={deptForm.code} onChange={e => setDeptForm(f => ({ ...f, code: e.target.value }))} /></div>
                <div><label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Office ID *</label>
                  <SelectField value={deptForm.officeId} onChange={e => setDeptForm(f => ({ ...f, officeId: e.target.value }))}>
                    <option value="">Select office…</option>
                    {offices.map(o => <option key={o.id} value={o.id}>{o.name} ({o.city})</option>)}
                  </SelectField></div>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                <AsyncButton onClick={() => createDept()} disabled={saving}>Create</AsyncButton>
                <button style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }} onClick={() => setShowDeptForm(false)}>Cancel</button>
              </div>
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Name", "Code", "Office", "Head", "Employees", ...(isAdmin ? ["Actions"] : [])].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deptPaged.pageItems.map(d => {
                const blockers = d._count.employees + d._count.children + d._count.workforcePlanEntries;
                return (
                  <tr key={d.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500 }}>{d.name}</td>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: "12px" }}>{d.code}</td>
                    <td style={{ padding: "10px 12px" }}>{d.office.name}</td>
                    <td style={{ padding: "10px 12px" }}>{d.head ? `${d.head.firstName} ${d.head.lastName}` : "—"}</td>
                    <td style={{ padding: "10px 12px" }}>{d._count.employees}</td>
                    {isAdmin && (
                      <td style={{ padding: "10px 12px" }}>
                        <button
                          onClick={() => deleteDept(d.id, d.name)}
                          disabled={blockers > 0}
                          title={blockers > 0 ? "Cannot delete — still referenced by employees, sub-departments, or workforce plans" : undefined}
                          style={{
                            ...btnStyle, fontSize: "11px", padding: "3px 10px",
                            background: blockers > 0 ? "var(--color-surface-100)" : "var(--color-error-bg)",
                            color: blockers > 0 ? "var(--color-surface-400)" : "var(--color-error-fg)",
                            cursor: blockers > 0 ? "not-allowed" : "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {departments.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 5} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No departments.</td></tr>
              )}
            </tbody>
          </table>
          <Pagination meta={deptPaged.meta} onPageChange={deptPaged.setPage} onPageSizeChange={setAdminPageSize} />
        </div>
      )}

      {/* ─── Offices ─── */}
      {!loading && tab === "Offices" && (
        <div>
          {isAdmin && !showOfficeForm && !editingOfficeId && (
            <button style={{ ...btnStyle, marginBottom: "16px" }} onClick={() => setShowOfficeForm(true)}>+ New Office</button>
          )}
          {(showOfficeForm || editingOfficeId) && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "14px" }}>{editingOfficeId ? "Edit Office" : "New Office"}</h2>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Address Line 1 (type to search)</label>
                <AddressAutocompleteInput
                  value={officeForm.addressLine1}
                  onChange={(v) => setOfficeForm(f => ({ ...f, addressLine1: v }))}
                  onResolve={(addr: ResolvedAddress) => setOfficeForm(f => ({
                    ...f,
                    addressLine1: addr.addressLine1,
                    city: addr.city || f.city,
                    pincode: addr.pincode,
                    geoLat: addr.lat,
                    geoLng: addr.lng,
                  }))}
                  placeholder="Start typing office address…"
                  style={inputStyle}
                />
                {officeForm.geoLat !== null && officeForm.geoLng !== null && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "11px", color: "var(--color-surface-500)", marginTop: "4px" }}>
                    <MapPin size={12} /> {officeForm.geoLat.toFixed(5)}, {officeForm.geoLng.toFixed(5)}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Address Line 2</label>
                <input style={inputStyle} value={officeForm.addressLine2} onChange={e => setOfficeForm(f => ({ ...f, addressLine2: e.target.value }))} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                {([
                  { label: "Name *", key: "name" },
                  { label: "City *", key: "city" },
                  { label: "Timezone *", key: "timezone" },
                  { label: "Currency", key: "currency" },
                  { label: "Pincode", key: "pincode" },
                ] as { label: string; key: keyof typeof officeForm }[]).map(({ label, key }) => (
                  <div key={key}>
                    <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>{label}</label>
                    <input style={inputStyle} value={(officeForm[key] ?? "").toString()} onChange={e => setOfficeForm(f => ({ ...f, [key]: e.target.value }))} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Country *</label>
                  <SelectField value={officeForm.country} onChange={e => setOfficeForm(f => ({ ...f, country: e.target.value }))}>
                    <option value="India">India</option>
                    <option value="US">US</option>
                  </SelectField>
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Geofence Radius (m)</label>
                  <input style={inputStyle} type="number" min="50" step="50" value={officeForm.geofenceRadiusM} onChange={e => setOfficeForm(f => ({ ...f, geofenceRadiusM: Number(e.target.value) }))} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Late Threshold (HH:MM)</label>
                  <TimeInput value={officeForm.lateThresholdHHMM} onChange={v => setOfficeForm(f => ({ ...f, lateThresholdHHMM: v }))} aria-label="Late threshold" />
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                <AsyncButton onClick={() => (editingOfficeId ? saveOfficeEdit() : createOffice())} disabled={saving}>
                  {editingOfficeId ? "Update" : "Create"}
                </AsyncButton>
                <button
                  style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }}
                  onClick={() => { setShowOfficeForm(false); setEditingOfficeId(null); setOfficeForm(blankOfficeForm); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Name", "City", "Country", "Timezone", "Currency", "Employees", "Depts", ...(isAdmin ? ["Actions"] : [])].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {officePaged.pageItems.map(o => (
                <tr key={o.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "10px 12px", fontWeight: 500 }}>
                    <div>{o.name}</div>
                    {o.addressLine1 && <div style={{ fontSize: "11px", color: "var(--color-surface-500)", marginTop: "2px" }}>{o.addressLine1}</div>}
                    {o.geoLat && <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "11px", color: "var(--color-surface-400)" }}><MapPin size={11} /> {o.geoLat.toFixed(4)}, {o.geoLng?.toFixed(4)}</div>}
                  </td>
                  <td style={{ padding: "10px 12px" }}>{o.city}</td>
                  <td style={{ padding: "10px 12px" }}>{o.country}</td>
                  <td style={{ padding: "10px 12px", fontSize: "12px" }}>{o.timezone}</td>
                  <td style={{ padding: "10px 12px" }}>{o.currency}</td>
                  <td style={{ padding: "10px 12px" }}>{o._count.employees}</td>
                  <td style={{ padding: "10px 12px" }}>{o._count.departments}</td>
                  {isAdmin && (
                    <td style={{ padding: "10px 12px", display: "flex", gap: 6 }}>
                      <button
                        style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: "var(--color-surface-100)", color: "var(--color-surface-700)" }}
                        onClick={() => {
                          setEditingOfficeId(o.id);
                          setShowOfficeForm(false);
                          setOfficeForm({
                            name: o.name, city: o.city, country: o.country,
                            timezone: o.timezone, currency: o.currency,
                            addressLine1: o.addressLine1 ?? "", addressLine2: o.addressLine2 ?? "",
                            pincode: o.pincode ?? "", geoLat: o.geoLat, geoLng: o.geoLng,
                            geofenceRadiusM: o.geofenceRadiusM, lateThresholdHHMM: o.lateThresholdHHMM,
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteOffice(o.id, o.name)}
                        disabled={o._count.employees + o._count.departments > 0}
                        title={o._count.employees + o._count.departments > 0 ? "Cannot delete — still referenced by employees or departments" : undefined}
                        style={{
                          ...btnStyle, fontSize: "11px", padding: "3px 10px",
                          background: o._count.employees + o._count.departments > 0 ? "var(--color-surface-100)" : "var(--color-error-bg)",
                          color: o._count.employees + o._count.departments > 0 ? "var(--color-surface-400)" : "var(--color-error-fg)",
                          cursor: o._count.employees + o._count.departments > 0 ? "not-allowed" : "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {offices.length === 0 && (
                <tr><td colSpan={isAdmin ? 8 : 7} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No offices.</td></tr>
              )}
            </tbody>
          </table>
          <Pagination meta={officePaged.meta} onPageChange={officePaged.setPage} onPageSizeChange={setAdminPageSize} />
        </div>
      )}

      {/* ─── Overhead Config ─── */}
      {!loading && tab === "Overhead Config" && (
        <div>
          <p style={{ fontSize: "13px", color: "var(--color-surface-500)", marginBottom: "16px" }}>
            Overhead rates applied to gross cost calculations. W2_USA=18%, C1099_USA=5%, INDIA=5% by default.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Category", "Rate (%)", "Effective From", isAdmin ? "Action" : ""].map((h, i) => h && (
                  <th key={i} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {overheadPaged.pageItems.map(o => (
                <tr key={o.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600, fontFamily: "monospace" }}>{o.category}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {isAdmin ? (
                      <input
                        style={{ ...inputStyle, width: "80px" }}
                        type="number" step="0.01" min="0" max="100"
                        value={overheadEdits[o.id] ?? o.ratePercent}
                        onChange={e => setOverheadEdits(prev => ({ ...prev, [o.id]: e.target.value }))}
                      />
                    ) : (
                      `${o.ratePercent}%`
                    )}
                  </td>
                  <td style={{ padding: "10px 12px" }}>{new Date(o.effectiveFrom).toLocaleDateString()}</td>
                  {isAdmin && (
                    <td style={{ padding: "10px 12px" }}>
                      <button style={{ ...btnStyle, fontSize: "12px", padding: "4px 12px" }} onClick={() => saveOverhead(o.id)} disabled={saving}>
                        Save
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination meta={overheadPaged.meta} onPageChange={overheadPaged.setPage} onPageSizeChange={setAdminPageSize} />
        </div>
      )}

      {/* ─── Holidays ─── */}
      {!loading && tab === "Holidays" && (
        <div>
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "center", flexWrap: "wrap" }}>
            <SelectField
              specular
              className="max-w-[120px]"
              value={holidayYear}
              onChange={async e => {
                const y = Number(e.target.value);
                setHolidayYear(y);
                setHolidays([]);
                try {
                  const r = await api.get<PublicHoliday[]>(`/admin/holidays?year=${y}`);
                  setHolidays(r.data);
                } catch (err) { logError("AdminPage.holidayYearChange", err); setError("Failed to load holidays"); }
              }}
            >
              {[2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
            </SelectField>
            {isAdmin && !showHolidayForm && !editingHolidayId && (
              <button style={btnStyle} onClick={() => setShowHolidayForm(true)}>+ New Holiday</button>
            )}
          </div>

          {(showHolidayForm || editingHolidayId) && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "14px" }}>{editingHolidayId ? "Edit Holiday" : "New Holiday"}</h2>
              <div style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: "12px", alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Date *</label>
                  <DatePicker style={inputStyle} value={holidayForm.date} onChange={v => setHolidayForm(f => ({ ...f, date: v }))} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Holiday Name *</label>
                  <input style={inputStyle} placeholder="e.g. Republic Day" value={holidayForm.name} onChange={e => setHolidayForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Optional</label>
                  <input type="checkbox" checked={holidayForm.isOptional} onChange={e => setHolidayForm(f => ({ ...f, isOptional: e.target.checked }))} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                <AsyncButton onClick={() => (editingHolidayId ? saveHolidayEdit() : createHoliday())} disabled={saving}>{editingHolidayId ? "Update" : "Create"}</AsyncButton>
                <button style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }} onClick={() => { setShowHolidayForm(false); setEditingHolidayId(null); setHolidayForm(blankHolidayForm); }}>Cancel</button>
              </div>
            </div>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Date", "Holiday", "Optional", ""].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {holidayPaged.pageItems.map(h => (
                <tr key={h.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "10px 12px", fontVariantNumeric: "tabular-nums", fontSize: "13px" }}>
                    {new Date(h.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })}
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 500 }}>{h.name}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {h.isOptional ? <span style={{ fontSize: "11px", background: "var(--color-warning-bg)", color: "var(--color-warning-fg)", padding: "2px 8px", borderRadius: "999px" }}>Optional</span>
                      : <span style={{ fontSize: "11px", background: "var(--color-success-bg)", color: "var(--color-success-fg)", padding: "2px 8px", borderRadius: "999px" }}>Mandatory</span>}
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {isAdmin && (
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: "var(--color-surface-100)", color: "var(--color-surface-700)" }}
                          onClick={() => { setEditingHolidayId(h.id); setHolidayForm({ date: h.date.split("T")[0]!, name: h.name, isOptional: h.isOptional }); setShowHolidayForm(false); }}
                        >Edit</button>
                        <button
                          style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: "var(--color-error-bg)", color: "var(--color-error-fg)" }}
                          onClick={() => deleteHoliday(h.id)} disabled={saving}
                        >Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {holidays.length === 0 && (
                <tr><td colSpan={4} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No holidays for {holidayYear}.</td></tr>
              )}
            </tbody>
          </table>
          <Pagination meta={holidayPaged.meta} onPageChange={holidayPaged.setPage} onPageSizeChange={setAdminPageSize} />
        </div>
      )}

      {/* ─── Dropdown Config ─── */}
      {!loading && tab === "Dropdown Config" && (
        <div>
          <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "center", flexWrap: "wrap" }}>
            <SelectField
              specular
              className="max-w-[240px]"
              value={lookupFilter}
              onChange={e => setLookupFilter(e.target.value)}
            >
              <option value="ALL">All Categories</option>
              {Object.entries(LOOKUP_CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </SelectField>
            {isAdmin && !showAddLookup && (
              <button style={btnStyle} onClick={() => setShowAddLookup(true)}>+ Add Value</button>
            )}
          </div>

          {showAddLookup && isAdmin && (
            <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "16px", marginBottom: "16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 80px", gap: "10px", alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Category *</label>
                  <SelectField value={addLookupForm.category} onChange={e => setAddLookupForm(f => ({ ...f, category: e.target.value }))}>
                    <option value="">Select…</option>
                    {Object.entries(LOOKUP_CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </SelectField>
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Value (code) *</label>
                  <input style={inputStyle} placeholder="e.g. HYDERABAD" value={addLookupForm.value} onChange={e => setAddLookupForm(f => ({ ...f, value: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Display Name *</label>
                  <input style={inputStyle} placeholder="e.g. Hyderabad" value={addLookupForm.displayName} onChange={e => setAddLookupForm(f => ({ ...f, displayName: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Rank</label>
                  <input style={inputStyle} type="number" value={addLookupForm.rank} onChange={e => setAddLookupForm(f => ({ ...f, rank: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <AsyncButton onClick={() => addLookupValue()} disabled={saving}>Add</AsyncButton>
                <button style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }} onClick={() => setShowAddLookup(false)}>Cancel</button>
              </div>
            </div>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)" }}>
                {["Category", "Display Name", "Value", "Rank", "Active", "Actions"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lookupPaged.pageItems
                .map(row => (
                  <tr key={row.id} style={{ borderBottom: "1px solid var(--color-surface-100)", opacity: row.isActive ? 1 : 0.5 }}>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: "11px", background: "var(--color-surface-100)", padding: "2px 8px", borderRadius: "999px" }}>
                        {LOOKUP_CATEGORY_LABELS[row.category] ?? row.category}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {editingLookupId === row.id ? (
                        <input style={{ ...inputStyle, padding: "4px 8px" }} value={editLookupForm.displayName}
                          onChange={e => setEditLookupForm(f => ({ ...f, displayName: e.target.value }))} />
                      ) : (
                        <span style={{ fontWeight: 500 }}>{row.displayName}</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: "12px", color: "var(--color-surface-500)" }}>{row.value}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {editingLookupId === row.id ? (
                        <input style={{ ...inputStyle, padding: "4px 8px", width: "60px" }} type="number" value={editLookupForm.rank}
                          onChange={e => setEditLookupForm(f => ({ ...f, rank: e.target.value }))} />
                      ) : (
                        row.rank
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: "12px", padding: "2px 8px", borderRadius: "999px",
                        background: row.isActive ? "var(--color-success-bg)" : "var(--color-error-bg)", color: row.isActive ? "var(--color-success-fg)" : "var(--color-error-fg)" }}>
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {isAdmin && (
                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                          {editingLookupId === row.id ? (
                            <>
                              <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px" }} onClick={() => saveLookupEdit(row.id)} disabled={saving}>Save</button>
                              <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: "var(--color-surface-200)", color: "var(--color-surface-700)" }}
                                onClick={() => setEditingLookupId(null)}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: "var(--color-surface-100)", color: "var(--color-surface-700)" }}
                                onClick={() => { setEditingLookupId(row.id); setEditLookupForm({ displayName: row.displayName, rank: String(row.rank) }); }}>
                                Edit
                              </button>
                              <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: row.isActive ? "var(--color-error-bg)" : "var(--color-success-bg)", color: row.isActive ? "var(--color-error-fg)" : "var(--color-success-fg)" }}
                                onClick={() => toggleLookupActive(row)} disabled={saving}>
                                {row.isActive ? "Deactivate" : "Activate"}
                              </button>
                              {!row.isSystem && (
                                <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 10px", background: "var(--color-error-bg)", color: "var(--color-error-fg)" }}
                                  onClick={() => deleteLookupValue(row.id)} disabled={saving}>
                                  Delete
                                </button>
                              )}
                              {row.isSystem && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "11px", color: "var(--color-surface-400)" }}><Lock size={11} /> system</span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              {lookupShown.length === 0 && (
                <tr><td colSpan={6} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No values found.</td></tr>
              )}
            </tbody>
          </table>
          <Pagination meta={lookupPaged.meta} onPageChange={lookupPaged.setPage} onPageSizeChange={setAdminPageSize} />
        </div>
      )}
      {tab === "Attendance" && <AttendanceAdminPanel />}

      {/* ─── Compensation Rules ─── */}
      {tab === "Compensation Rules" && <CompRulesPanel role={role} />}

      {/* ─── Policy Documents (handbook, leave policy, and types HR adds) ─── */}
      {!loading && tab === "Policy Documents" && <PolicyDocumentsPanel />}

    </div>
  );
}

function RoleRow({ emp, isAdmin, onSave, saving }: {
  emp: EmpRow; isAdmin: boolean;
  onSave: (id: string, role: string) => Promise<void>;
  saving: boolean;
}) {
  const [selected, setSelected] = useState(emp.role);
  const roleLabels = useLookup("ROLE_LABEL");

  return (
    <tr style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
      <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: "12px" }}>{emp.employeeCode}</td>
      <td style={{ padding: "10px 12px" }}>{emp.firstName} {emp.lastName}</td>
      <td style={{ padding: "10px 12px", fontSize: "12px" }}>{emp.email}</td>
      <td style={{ padding: "10px 12px" }}>{emp.department?.name ?? "—"}</td>
      <td style={{ padding: "10px 12px" }}>
        <span style={{ fontSize: "12px", background: "var(--color-info-bg)", color: "var(--color-info-fg-strong)", padding: "2px 8px", borderRadius: "999px" }}>{emp.role}</span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        {isAdmin ? (
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <SelectField
              size="sm"
              className="w-[150px] min-w-0 shrink-0"
              value={selected}
              onChange={e => setSelected(e.target.value)}
            >
              {roleLabels.map(o => (
                <option key={o.value} value={o.value}>{o.displayName}</option>
              ))}
            </SelectField>
            {selected !== emp.role && (
              <button
                style={{ padding: "4px 10px", borderRadius: "var(--radius-md)", fontSize: "12px", fontWeight: 600, border: "none", background: "var(--color-brand-600)", color: "#fff", cursor: "pointer" }}
                onClick={() => onSave(emp.id, selected)}
                disabled={saving}
              >
                Save
              </button>
            )}
          </div>
        ) : (
          <span style={{ fontSize: "12px", color: "var(--color-surface-400)" }}>Read-only</span>
        )}
      </td>
    </tr>
  );
}

